import * as React from 'react';
export function useControlled({ controlledValue, defaultValue, }) {
    const { current: isControlled } = React.useRef(controlledValue !== undefined);
    const [valueState, setValue] = React.useState(defaultValue);
    const value = isControlled ? controlledValue : valueState;
    const setValueIfUncontrolled = React.useCallback((newValue) => {
        if (!isControlled) {
            setValue(newValue);
        }
    }, [isControlled]);
    return [value, setValueIfUncontrolled];
}
export function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = React.useState(value);
    React.useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}
export function useDisclosure(initial = false) {
    const [isOpen, setIsOpen] = React.useState(initial);
    const open = React.useCallback(() => setIsOpen(true), []);
    const close = React.useCallback(() => setIsOpen(false), []);
    const toggle = React.useCallback(() => setIsOpen((state) => !state), []);
    return { isOpen, open, close, toggle };
}
export function usePrevious(value) {
    const ref = React.useRef();
    React.useEffect(() => {
        ref.current = value;
    });
    return ref.current;
}
export function useWhyDidYouUpdate(name, props) {
    const previousProps = React.useRef();
    React.useEffect(() => {
        if (previousProps.current) {
            const allKeys = Object.keys(Object.assign(Object.assign({}, previousProps.current), props));
            const changesObj = {};
            allKeys.forEach((key) => {
                if (previousProps.current[key] !== props[key]) {
                    changesObj[key] = {
                        from: previousProps.current[key],
                        to: props[key],
                    };
                }
            });
            if (Object.keys(changesObj).length) {
                console.log('[why-did-you-update]', name, changesObj);
            }
        }
        previousProps.current = props;
    });
}
export const useResizeObserver = (ref, callback) => {
    const [width, setWidth] = React.useState(0);
    const [height, setHeight] = React.useState(0);
    const handleResize = React.useCallback((entries) => {
        if (!Array.isArray(entries) || !entries.length) {
            return;
        }
        const entry = entries[0];
        setWidth(entry.contentRect.width);
        setHeight(entry.contentRect.height);
        if (callback) {
            callback(entry.contentRect);
        }
    }, [callback]);
    React.useEffect(() => {
        if (!ref || !ref.current || !window.ResizeObserver) {
            return;
        }
        const RO = new window.ResizeObserver((entries) => window.requestAnimationFrame(() => handleResize(entries)));
        RO.observe(ref.current);
        return () => {
            RO.disconnect();
        };
    }, [ref, handleResize]);
    return [width, height];
};
//# sourceMappingURL=index.js.map